<template>
  <PortalWidget
    :widget="{
      title: widgetTitle,
      iconClass: 'typcn typcn-phone'
    }"
    class="numbers-list"
  >
    <template v-slot:headerContent>
      <div
        v-if="!vm.showSpecificRecordDetails"
        class="header-extra"
      >
        <ColumnToggle
          :table-name="'numbers'"
          :user-settings="userSettings"
          :settings-status="getUserSettingsStatus"
          :column-settings="vm.columnSettings"
          @onChange="vm.syncColumns()"
        />
        <button
          v-t="'NUMBER_POOLS_EXPORT_BUTTON_CSV'"
          class="btn btn-sm widget-header-button"
          @click="vm.exportNumbersCSV()"
        />
        <SearchableDropdown
          :value="$t(vm.selectedOptionDropdown)"
          :searchable="false"
          :config="filterConfig"
          class="m-r-10 btn-xl pull-right widget-header-dropdown"
          @change="vm.onFilterChange($event)"
        />
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>

    <div
      v-if="!vm.isLoading"
      class="m-t-10 m-b-15"
    >
      <button
        v-if="vm.hasNumberPermissionAccess && !vm.showSpecificRecordDetails && !vm.isAffiliateManager"
        type="button"
        :disabled="vm.isCreateMode"
        class="btn btn-success btn-sm"
        @click.prevent="vm.showForm(null)"
      >
        <i class="typcn typcn-plus" />
        <span v-t="'NUMBER_CREATE_BUTTON'" />
      </button>

      <a
        v-if="vm.showSpecificRecordDetails"
        :href="getHref('dashboard.numbers')"
        class="btn btn-default btn-sm"
      >
        <i class="fa fa-arrow-left" />
        <span v-t="'NUMBERS_SHOW_ALL'" />
      </a>

      <NumbersOrdersButton
        v-if="vm.isAdmin"
        class="right"
      />
    </div>

    <div class="grid_table_holder">
      <v-table
        v-show="displayNumbers.length || vm.isCreateMode"
        :data="displayNumbers"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        cellpadding="0"
        cellspacing="0"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobileView">
            <!-- Empty header for actions -->
            </th>

            <v-th
              v-for="col in activeColumns"
              :key="col"
              v-t="vm.columnSettings[col].displayName"
              :class="vm.columnSettings[col].class"
              :default-sort="vm.columnSettings[col].defaultSort"
              :sort-key="vm.columnSettings[col].name"
            />

            <th
              v-if="!isMobileView"
              v-t="'NUMBERS_ACTIONS'"
            />
          </tr>
        </thead>

        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <tr v-if="!vm.isCreateMode && !displayData.length">
            <td
              v-t="'NUMBERS_HEADER_NONE'"
              colspan="100"
            />
          </tr>

          <!-- Add new number form row - START -->
          <tr
            v-if="vm.isCreateMode"
            class="row-inline-new-form slide-down"
          >
            <td colspan="100">
              <ManageNumbers
                :form-name="'create'"
                @cancel="vm.hideForm()"
                @submit="vm.onSave()"
              />
            </td>
          </tr>
          <!-- Add new number form row - END -->

          <template
            v-for="number in displayData"
          >
            <tr
              :key="'data_' + number.id"
              class="border_bottom height-42"
            >
              <td v-if="isMobileView">
                <a
                  class="btn btn-list-function btn-expand-actions"
                  @click="vm.showForm(number)"
                >
                  <i class="fa fa-ellipsis-v" />
                </a>
              </td>

              <td
                v-for="col in activeColumns"
                :key="col"
                :align="vm.columnSettings[col].rowAlign"
              >
                <span v-if="col === 'allocationDT'">
                  {{ utcToLocal(number.allocationDT) }}
                </span>
                <span v-else-if="col === 'nextChargeDT'">
                  {{ utcToLocal(number.nextChargeDT) }}
                </span>
                <span v-else-if="col === 'country'">
                  <CountryFlag
                    :country-code="number.countryCode"
                  />
                </span>
                <span
                  v-else-if="col === 'phoneNumber'"
                  class="clickable"
                >
                  <a
                    :href="getHref('dashboard.numbers.edit', { numberId: number.id })"
                    @click.prevent="vm.showForm(number)"
                  >
                    {{ number.phoneNumber }}
                  </a>

                  <i
                    v-if="number.isInternalOnly"
                    v-tooltip.right.hover="$t('INTERNAL_USE_NUMBER')"
                    class="fa fa-lock p-l-5"
                  />
                </span>
                <span v-else-if="col === 'name'">
                  {{ number.name }}
                </span>

                <span v-else-if="col === 'displayNumber'">
                  {{ number.displayNumber }}
                </span>

                <span v-else-if="col === 'status'">
                  <span
                    v-tooltip.right.hover="$t(number.enabled && !vm.currentAccountIsPaused ? 'ENABLED' : 'PAUSED')"
                    class="color-dot"
                    :class="{
                      'yellow_status': !number.enabled || vm.currentAccountIsPaused,
                      'green_status': number.enabled && !vm.currentAccountIsPaused
                    }"
                  />
                </span>

                <span v-else-if="col === 'campaignId'">

                  <!-- Link to incomplete campaign to be shown to Admins only - START -->
                  <LinkedName
                    v-if="vm.isAdmin && number.campaignName && !number.isCampaignComplete"
                    :href="getHref('dashboard.campaign-wizard', { campaignId: number.campaignId })"
                    :title="number.campaignName"
                  />
                  <!-- Link to incomplete campaign to be shown to Admins only - END -->

                  <!-- Link to complete campaign - START -->
                  <LinkedName
                    v-if="number.campaignName && number.isCampaignComplete"
                    :href="getHref('dashboard.campaign', { campaignId: number.campaignId })"
                    :title="number.campaignName"
                  />
                  <!-- Link to complete campaign - END -->

                  <!-- Text for incomplete campaign to be shown to non-admins only - START -->
                  <span
                    v-if="!vm.isAdmin && number.campaignName && !number.isCampaignComplete"
                  >
                    {{ number.campaignName }}
                  </span>
                  <!-- Text for incomplete campaign to be shown to non-admins only - END -->

                  <!-- No campaign exists - START -->
                  <span v-if="!number.campaignName">
                    <i
                      v-tooltip.right.hover="$t('NUMBERS_LINK_CAMPAIGN_TT')"
                      class="false typcn typcn-minus"
                    />
                  </span>
                  <!-- No campaign exists - END -->
                </span>


                <span v-else-if="col === 'numberPoolId'">
                  <span v-if="number.numberPoolId">
                    {{ number.numberPoolName }}
                  </span>
                  <button
                    v-if="!number.numberPoolId"
                    class="btn btn-function"
                    :disabled="number.campaignId || number.publisherId"
                    :class="{ 'opacity-50': number.campaignId || number.publisherId }"
                    @click="vm.selectNumberPoolModal(number)"
                  >
                    <i
                      v-tooltip.right.hover="$t((number.campaignId || number.publisherId) ? 'NUMBERS_LINK_POOL_TT' : 'NUMBERS_ASSIGN_NP_TT')"
                      class="fa fa-fw fa-plus"
                    />
                  </button>
                </span>

                <span v-else-if="col === 'offer'">
                  <span
                    v-tooltip.right.hover="$t('NUMBER_OFFER_LINK')"
                  >
                    {{ number.offerName }}
                  </span>
                </span>

                <span v-else-if="col === 'affiliate'">
                  <span>
                    <span v-if="number.publisherId">

                      <span
                        v-if="!vm.isAdmin"
                      >
                        {{ number.publisherName }}
                      </span>

                      <LinkedName
                        v-if="vm.isAdmin"
                        :href="getHref('dashboard.affiliate.edit', { affiliateId: number.publisherId, name: number.publisherName })"
                        :title="number.publisherName"
                      >
                        <a
                          href
                          class="m-r-5"
                          @click.prevent="vm.removeAffiliate(number)"
                        >
                          <i
                            v-tooltip.right.hover="$t('UNASSIGN_PUBLISHER_FROM_NUMBER')"
                            class="fa fa-fw fa-times"
                            tooltip-class="tooltip-white"
                            tooltip-append-to-body="true"
                          />
                        </a>
                      </LinkedName>
                    </span>

                    <span v-if="!number.publisherId">
                      <a
                        class="btn btn-function"
                        @click="vm.selectAffiliateModal(number)"
                      >
                        <i
                          v-tooltip.right.hover="$t('NUMBERS_ASSIGN_TT')"
                          class="fa fa-fw fa-plus"
                        />
                      </a>
                    </span>
                  </span>
                </span>
              </td>
              <td v-if="!isMobileView">
                <NumberActions
                  :number="number"
                />
              </td>
            </tr>

            <!-- Actions Mobile collapsed view - Start -->
            <tr
              v-if="isMobileView && vm.getIsEditingNumberId(number.id)"
              :key="'actions_' + number.id"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <NumberActions
                  :number="number"
                />
              </td>
            </tr>
            <!-- Actions Mobile collapsed view - End -->

            <tr
              v-if="vm.getIsEditingNumberId(number.id)"
              :key="'edit_' + number.id"
              class="slide-down row-inline-new-form"
              ng-class-odd="'bg-row-odd'"
              ng-class-even="'bg-row-even'"
            >
              <!-- Edit number pool form row - START -->
              <td
                colspan="100"
              >
                <ManageNumbers
                  :form-name="'edit'"
                  :current-number="number"
                  class="p-t-20"
                  @cancel="vm.hideForm()"
                  @submit="vm.onSave()"
                />
              </td>
              <!-- Edit number pool form row - END -->
            </tr>
          </template>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
    </div>

    <div
      v-if="vm.isLoading"
      class="text-center empty-records"
    >
      <Loader />
      <span>{{ loadingTitle }}</span>
    </div>

    <template
      v-if="vm.modal"
    >
      <NumberPauseModal
        v-if="vm.modal.type === 'confirm-pause'"
        :number="vm.modal.number"
        @close="vm.cancelModal()"
        @confirm="vm.setNumberStatus(null, false)"
      />

      <NumberDeleteModal
        v-else-if="vm.modal.type === 'confirm-delete'"
        :number="vm.modal.number"
        :number-references="vm.numberReferences"
        :is-loading="vm.numberReferencesState === 'loading'"
        :on-confirm="vm.modal.onSubmit"
        @close="vm.cancelModal()"
      />

      <NumberSelectPoolModal
        v-else-if="vm.modal.type === 'select-pool'"
        :selected-number="vm.modal.number"
        :pools-list="vm.numberPools"
        :on-submit="args => vm.attachNumberPool(args.poolId, args.numberId)"
        @cancel="vm.cancelModal()"
      />

      <NumberSelectAffiliateModal
        v-else-if="vm.modal.type === 'select-affiliate'"
        :selected-number="vm.modal.number"
        :affiliates-list="vm.affiliates"
        :on-submit="args => vm.attachAffiliate(args.affiliateId, args.numberId)"
        @cancel="vm.cancelModal()"
      />

      <ConfirmationModal
        v-else-if="vm.modal.type === 'confirm-remove-affiliate'"
        :entity="vm.modal.number"
        :title="$t('NUMBER_REMOVE_AFFILIATE')"
        :cancel-button-text="$t('CANCEL')"
        :confirm-action-text="$t('NUMBER_REMOVE_AFFILIATE_CONFIRM')"
        :on-confirm="vm.modal.onSubmit"
        @close="vm.cancelModal()"
      >
        <p
          v-t="'NUMBER_REMOVE_AFFILIATE_TEXT'"
        />
      </ConfirmationModal>
    </template>
  </PortalWidget>
</template>

<script>

import Loader from '@/core/components/Loader/index.vue';
import PortalWidget from '@/core/components/PortalWidget.vue';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle.vue';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown.vue';
import NumbersOrdersButton from '../components/orders/NumbersOrdersButton.vue';
import CountryFlag from '@/core/components/CountryFlag/CountryFlag.vue';
import LinkedName from '@/core/components/LinkedName/LinkedName.vue';
import ManageNumbers from '../components/numbers/ManageNumbers.vue';
import NumberActions from '../components/numbers/NumberActions.vue';
import { defineComponent, reactive } from 'vue';
import sharedMessages from '@/core/locales';
import messages from '../locales';
import { mapActions, mapGetters, mapState } from 'vuex';
import { isMobile } from '@/core/utilities';
import { utcToLocalFormat } from '@/core/filters/utcToLocalFormat';
import { NumbersViewModel } from '../models/NumbersViewModel';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal.vue';
import NumberPauseModal from '../components/numbers/NumberPauseModal.vue';
import NumberDeleteModal from '../components/numbers/NumberDeleteModal.vue';
import NumberSelectPoolModal from '../components/numbers/NumberSelectPoolModal.vue';
import NumberSelectAffiliateModal from '../components/numbers/NumberSelectAffiliateModal.vue';
import {
  $ngStateService,
  utilities,
} from '@/angularBridgeCode/ringbaPortal.servicesModule';

export default defineComponent({
  components: {
    PortalWidget,
    ColumnToggle,
    Loader,
    SearchableDropdown,
    NumbersOrdersButton,
    ConfirmationModal,
    NumberPauseModal,
    NumberDeleteModal,
    NumberSelectPoolModal,
    NumberSelectAffiliateModal,
    ManageNumbers,
    NumberActions,
    CountryFlag,
    LinkedName,
  },
  data() {
    const model = new NumbersViewModel();
    return {
      vm: reactive(model),
      isMobileView: isMobile,
      currentPage: 1,
      totalPages: 0,

      filters: {
        name: {
          value: '',
          keys: ['number', 'phoneNumber', 'displayNumber', 'name', 'campaignName', 'publisherName'],
        },
      },
    };
  },
  provide() {
    return {
      numberContext: this.vm,
    };
  },
  computed: {
    selectedNumberId() {
      return utilities.getPropFromUrlParams('numberId');
    },
    widgetTitle() {
      return this.vm.selectedNumber
        ? `Number: ${this.vm.selectedNumber.phoneNumber}`
        : this.$t('MENU_LEFT_NUMBERS_VIEW');
    },
    loadingTitle() {
      return this.vm.showSpecificRecordDetails
        ? this.$t('NUMBER_HEADER_LOADING')
        : this.$t('NUMBERS_HEADER_LOADING');
    },
    ...mapState('ui', ['userSettings']),
    ...mapGetters('ui', ['getUserSettingsStatus']),
    activeColumns() {
      return this.vm.getActiveColumns();
    },
    filterConfig() {
      return {
        placeholder: '',
        options: this.vm.getFilterOptions().map(option => ({
          label: this.$t(option),
          value: option,
        })),
      };
    },
    displayNumbers() {
      return this.vm.getDisplayNumbers();
    },
  },
  watch: {
    displayNumbers() {
      if (!this.vm.showSpecificRecordDetails) {
        this.vm.hideForm(false);
      }
    },
  },
  methods: {
    ...mapActions('ui', ['getUserSettings']),
    utcToLocal: d => utcToLocalFormat(d, 'MMM DD, YYYY'),
    getHref(state, params) {
      return $ngStateService.href(
        state,
        params,
        { absolute: true }
      );
    },
  },
  mounted() {
    this.vm
      .withI18N((key) => this.$t(key))
      .withSelectedNumberId(this.selectedNumberId)
      .initialize();
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
});

</script>

<style lang="scss">

  .numbers-list .v-select {
    .vs__dropdown-toggle {
      padding: 2px;
    }

    .vs__selected {
      font-size: 13px;
    }
  }
</style>
